import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import {
  createThreadChunkQueryOptions,
  createThreadStatePrefetchOptions,
} from "@/features/thread/hooks/threadQueryOptions";
import { apiGet, hasAimgToken, useAimgViewerContext } from "@/shared/api";

/** Mobile catalog cells only warm the public first chunk and do not observe viewer state. */
export function usePrefetchThreadChunk(): (threadId: string) => void {
  const queryClient = useQueryClient();

  return useCallback(
    (threadId: string): void => {
      if (!threadId) return;
      void queryClient.prefetchQuery(
        createThreadChunkQueryOptions(threadId, 0, apiGet),
      );
    },
    [queryClient],
  );
}

/**
 * カタログセルの先読み入力時に、スレッドを開くのに必要な先頭公開chunkと
 * stateクエリを温める。chunkは`useThread`と同一のキー生成関数
 * (`threadChunkQueryKey`)・同一のリクエスト組み立て・同一のfetcher
 * (`apiGet`)を経由するため、開いたときに同じQuery cacheを再利用する。
 * stateは`useThread`の stateQuery と同一のキー生成関数
 * (`threadStateQueryKey`)・同一のリクエスト組み立て(`createThreadStateRequest`)・
 * 同一のfetcher(`apiGet`)を経由するため、viewer context世代を含むキーが
 * `useThread` 側と必ず一致する。クリック時に先読みが進行中なら同じrequestを
 * 再利用する。stateの完了済みcacheは現在contextのstateとして再検証し、mount後の
 * 取得成功までは表示判定に使用しない。
 * 先頭公開chunkは匿名でも取得できるため常に先読みする。stateはtoken未発行の
 * 閲覧者には先読みしない。hoverやtouchのような受動的操作からPOST /tokens
 * (フィンガープリント送信)を新規発行しないため、`hasAimgToken()`で既発行を
 * 確認できた場合のみstateを先読みする。
 */
export function usePrefetchThreadState(): (threadId: string) => void {
  const prefetchThreadChunk = usePrefetchThreadChunk();
  const queryClient = useQueryClient();
  const viewerContext = useAimgViewerContext();

  return useCallback(
    (threadId: string): void => {
      if (!threadId) return;
      prefetchThreadChunk(threadId);
      if (!hasAimgToken()) return;
      void queryClient.prefetchQuery(
        createThreadStatePrefetchOptions(threadId, viewerContext, apiGet),
      );
    },
    [prefetchThreadChunk, queryClient.prefetchQuery, viewerContext],
  );
}
